import React, { useEffect, useState } from "react";
import { Link2, Copy, Trash2, CheckCircle2, Clock, ExternalLink } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";

const fmt = (iso) => (iso ? new Date(iso).toLocaleDateString("en-AU", { day: "numeric", month: "short" }) : "");

export const InvitesPanel = ({ onReview }) => {
  const [invites, setInvites] = useState([]);
  const [form, setForm] = useState({ business_name: "", contact_email: "", note: "" });
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/admin/invites").then((r) => setInvites(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const linkFor = (inv) => `${window.location.origin}/submit/${inv.token}`;
  const copy = (inv) => { navigator.clipboard?.writeText(linkFor(inv)); toast.success("Invite link copied — send it to the business."); };

  const create = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/admin/invites", form);
      setForm({ business_name: "", contact_email: "", note: "" });
      load();
      copy(data);
    } catch (err) { toast.error(err.response?.data?.detail || "Couldn't create invite."); }
    finally { setBusy(false); }
  };

  const remove = async (inv) => {
    if (!window.confirm("Delete this invite link? It will stop working immediately.")) return;
    await api.delete(`/admin/invites/${inv.id}`);
    load();
  };

  const input = "w-full bg-white border border-[color:var(--line)] rounded-xl px-4 py-2.5 text-sm outline-none focus:border-[color:var(--ocean)]";
  return (
    <div className="grid grid-cols-1 lg:grid-cols-5 gap-8" data-testid="invites-panel">
      <form onSubmit={create} className="lg:col-span-2 bg-white rounded-2xl border border-[color:var(--line)] p-6 space-y-4 self-start">
        <div className="font-display text-2xl inline-flex items-center gap-2"><Link2 size={18} /> Invite a business</div>
        <p className="text-xs text-[color:var(--ink-2)]">Creates a private, single-use link. The business fills in their own listing; it lands in Tried & Tested as a hidden draft for you to review and publish. The link isn't discoverable anywhere on the public site.</p>
        <input data-testid="invite-business" required placeholder="Business name" value={form.business_name} onChange={(e) => setForm({ ...form, business_name: e.target.value })} className={input} />
        <input data-testid="invite-email" type="email" placeholder="Contact email (optional)" value={form.contact_email} onChange={(e) => setForm({ ...form, contact_email: e.target.value })} className={input} />
        <input data-testid="invite-note" placeholder="Note shown to them (optional)" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} className={input} />
        <button disabled={busy} data-testid="invite-create-btn" className="btn-pill btn-primary w-full justify-center disabled:opacity-50">{busy ? "Creating…" : "Create & copy invite link"}</button>
      </form>
      <div className="lg:col-span-3 rounded-2xl border border-[color:var(--line)] bg-white overflow-hidden">
        <div className="px-5 py-3 border-b border-[color:var(--line)] text-[0.68rem] uppercase tracking-[0.14em] font-bold text-[color:var(--ink-2)]">Invites ({invites.length})</div>
        {invites.length === 0 && <div className="py-10 text-center text-sm text-[color:var(--ink-2)]" data-testid="invites-empty">No invites yet.</div>}
        {invites.map((inv) => (
          <div key={inv.id} data-testid={`invite-row-${inv.id}`} className="flex items-center justify-between gap-3 px-5 py-3 border-b border-[color:var(--line)] last:border-0">
            <div className="min-w-0">
              <div className="font-semibold text-sm text-[color:var(--ocean)] truncate">{inv.business_name || "Untitled"} {inv.submitted_name && inv.submitted_name !== inv.business_name && <span className="text-xs font-normal text-[color:var(--ink-2)]">→ {inv.submitted_name}</span>}</div>
              <div className="text-xs text-[color:var(--ink-2)] truncate">{inv.contact_email || "no email"} · sent {fmt(inv.created_at)} by {inv.created_by}</div>
            </div>
            <div className="flex items-center gap-1 flex-shrink-0">
              {inv.used_at ? (
                <>
                  <span className="chip !bg-emerald-100 !text-emerald-800 !border-emerald-200 inline-flex items-center gap-1"><CheckCircle2 size={11} /> Submitted</span>
                  <button onClick={() => onReview(inv.business_id)} data-testid={`invite-review-${inv.id}`} title="Review draft listing" className="p-2 rounded-full hover:bg-[color:var(--sand)]"><ExternalLink size={15} /></button>
                </>
              ) : (
                <>
                  <span className="chip inline-flex items-center gap-1"><Clock size={11} /> Awaiting</span>
                  <button onClick={() => copy(inv)} data-testid={`invite-copy-${inv.id}`} title="Copy link" className="p-2 rounded-full hover:bg-[color:var(--sand)]"><Copy size={15} /></button>
                  <Link to={`/submit/${inv.token}`} target="_blank" title="Preview form" className="p-2 rounded-full hover:bg-[color:var(--sand)]"><ExternalLink size={15} /></Link>
                </>
              )}
              <button onClick={() => remove(inv)} data-testid={`invite-delete-${inv.id}`} title="Delete" className="p-2 rounded-full hover:bg-red-50 text-red-600"><Trash2 size={15} /></button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
